import React, {useRef, Fragment, forwardRef, useCallback, useEffect, useImperativeHandle} from "react";
import {useSelector} from "react-redux";
import PropTypes from "prop-types";

import {Translation as T} from "@awe-web/shared/lib";
import {
  rentObjectShape,
  RentObjectNameAndPrice,
  RentObjectBasicProperties,
  RentObjectActions
} from "./rent_object.js";
import {RentObjectDetailsButton} from "./rent_object_overlay.js";
import {Button} from "../../components/form.js";
import {InlineLoadingIndicator} from "../../components/loading_indicator.jsx";
import {getCurrentHeaderHeight} from "../../dom/store.js";

import "./rent_object_card.scss";

const SHOW_DETAILS = "FSD0000113";
const SELECT_RENT_OBJECT = "FSD0000114";
const RENT_OBJECTS_LOADING = "FSD0000357";

function RentObjectCard({rentObject, selectedBasicProperties, onSelectedRentObject}) {
  const detailsButtonRef = useRef();

  const onCardClickHandler = useCallback(() => {
    if (detailsButtonRef.current) {
      detailsButtonRef.current.openOverlay();
    }
  }, []);

  const onClickHandler = useCallback((event) => {
      // Prevent event from opening the overlay
      event.stopPropagation();
      if (detailsButtonRef.current) {
        detailsButtonRef.current.closeOverlay();
      }
      onSelectedRentObject(rentObject.classificationId);
  }, [onSelectedRentObject, rentObject?.classificationId]);

  const renderSelectButton = useCallback(() => (
    <Button
      primary
      continue
      className="rent-object-card__select-button"
      onClick={onClickHandler}
    >
      <T code={SELECT_RENT_OBJECT} />
    </Button>
  ), [onClickHandler]);

  if (!rentObject) {
    return;
  }

  return (
    <div className="rent-object-card rent-object-card-list__rent-object-card" onClick={onCardClickHandler}>
      <RentObjectNameAndPrice className="rent-object-card__heading" rentObject={rentObject} />

      <div className="rent-object-card__image">
        <img className="rent-object-card__image-tag" src={rentObject.imageUrl1} alt={rentObject.description} />
      </div>

      <div className="rent-object-details">
        <RentObjectBasicProperties
          rentObject={rentObject}
          selectedBasicProperties={selectedBasicProperties}
        />
        <RentObjectActions>
          <RentObjectDetailsButton
            ref={detailsButtonRef}
            rentObject={rentObject}
            selectedBasicProperties={selectedBasicProperties}
            renderButton={({className, openOverlay}) => (
              <Button
                secondary
                continue
                className={className}
                onClick={openOverlay}
              >
                <T code={SHOW_DETAILS} />
              </Button>
            )}
            actionButton={renderSelectButton()} />
          {renderSelectButton()}
        </RentObjectActions>
      </div>
    </div>
  );
}

RentObjectCard.propTypes = {
  rentObject: rentObjectShape.isRequired,
  selectedBasicProperties: RentObjectBasicProperties.propTypes.selectedBasicProperties,
  onSelectedRentObject: PropTypes.func
};

export const RentObjectCardList = forwardRef(({
  groupedRentObjects = {},
  selectedBasicProperties,
  onSelectedRentObject = () => {}
}, ref) => {

  const cardMargin = useRef(0);
  const elementRef = useRef();
  const headerHeight = useSelector(getCurrentHeaderHeight);
  
  useImperativeHandle(ref, () => ({
    scrollToCard: (elementId) => {
      const anchorNode = elementRef.current.querySelector(`#${elementId}`);
      const top = anchorNode.offsetTop - headerHeight - cardMargin.current;

      window.scrollTo({
        top,
        behavior: "smooth"
      });
    }
  }));

  useEffect(() => {
    if (!elementRef.current) {
      return;
    }
    const rentObjectCard = elementRef.current.querySelector(".rent-object-card-list__rent-object-card");

    if (rentObjectCard) {
      cardMargin.current = parseInt(window.getComputedStyle(rentObjectCard).marginBottom);
    }
  }, []);

  return (
    <div ref={elementRef} className="rent-object-card-list">
      <InlineLoadingIndicator typePrefix="RENT_OBJECTS/RENT_OBJECTS" message={<T code={RENT_OBJECTS_LOADING} />}>
        {Object.keys(groupedRentObjects).map(elementId => (
          <Fragment key={elementId}>
            <div id={elementId} />

            {groupedRentObjects[elementId].map(rentObject => (
              <RentObjectCard
                key={rentObject.id}
                rentObject={rentObject}
                selectedBasicProperties={selectedBasicProperties}
                onSelectedRentObject={onSelectedRentObject} />
            ))}
          </Fragment>
        ))}
      </InlineLoadingIndicator>
    </div>
  );
});

RentObjectCardList.propTypes = {
  groupedRentObjects: PropTypes.objectOf(
    PropTypes.arrayOf(RentObjectCard.propTypes.rentObject)
  ),
  selectedBasicProperties: RentObjectCard.propTypes.selectedBasicProperties,
  onSelectedRentObject: PropTypes.func,
};
